import React, { type FC } from 'react'
import { useCallback, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Link,
  Typography,
} from '@mui/material'
import { Lock, RestorePageOutlined } from '@mui/icons-material'
import { WizardHeader, StorageModeCheckbox } from '@/common/components'
import { useCrypto, createKeyRing, exportKeyRingToFile } from '@/infra/crypto'
import type { StorageMode } from '@/infra/crypto/key-ring'
import { useUserInitializationContext, useAuthStatus } from '@/features/user'
import { useOnboardingUpdate } from '../../hooks'

interface KeyRingSetupStepProps {
  readonly isReturningUser: boolean
  readonly onStepComplete: () => void
}

type StepPhase = 'idle' | 'generating' | 'importing' | 'saving' | 'success' | 'error'

const MAX_KEY_RING_FILE_SIZE = 1024 * 1024

export const KeyRingSetupStep: FC<KeyRingSetupStepProps> = ({
  isReturningUser,
  onStepComplete,
}) => {
  const { loadKeyRing, importKeyRing, setStorageMode } = useCrypto()
  const { updateOnboardingStep } = useUserInitializationContext()
  const { user } = useAuthStatus()
  const userEmail = user?.profile?.email ?? ''

  const {
    updateStepAsync,
    isUpdating,
    error: onboardError,
    reset: resetOnboard,
  } = useOnboardingUpdate()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [phase, setPhase] = useState<StepPhase>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showImportMode, setShowImportMode] = useState(isReturningUser)
  const [showLostKeysDialog, setShowLostKeysDialog] = useState(false)
  const [selectedStorageMode, setSelectedStorageMode] = useState<StorageMode>('memory')

  const handleGenerate = useCallback(async () => {
    if (!userEmail.trim()) return

    setPhase('generating')
    setErrorMessage(null)

    try {
      const keyRing = await createKeyRing(userEmail.trim())
      loadKeyRing(keyRing)

      if (selectedStorageMode === 'localStorage') {
        setStorageMode('localStorage')
      }

      exportKeyRingToFile(keyRing)

      setPhase('saving')
      const result = await updateStepAsync({ keyRingConfigured: true })

      if (result && typeof result === 'object' && 'success' in result && result.success === false) {
        setPhase('error')
        setErrorMessage(
          'Keys generated successfully, but progress could not be saved. Your keys are ready to use.',
        )
        return
      }

      updateOnboardingStep('keyRingConfigured')
      setPhase('success')
      onStepComplete()
    } catch {
      setPhase('error')
      setErrorMessage(
        'Keys generated successfully, but progress could not be saved. Your keys are ready to use.',
      )
    }
  }, [
    userEmail,
    loadKeyRing,
    updateStepAsync,
    updateOnboardingStep,
    onStepComplete,
    selectedStorageMode,
    setStorageMode,
  ])

  const handleImport = useCallback(
    async (file: File) => {
      setPhase('importing')
      setErrorMessage(null)

      try {
        await importKeyRing(file)

        if (selectedStorageMode === 'localStorage') {
          setStorageMode('localStorage')
        }

        setPhase('success')
        onStepComplete()
      } catch (err) {
        setPhase('error')
        setErrorMessage(err instanceof Error ? err.message : 'Failed to import key ring file.')
      }
    },
    [importKeyRing, onStepComplete, selectedStorageMode, setStorageMode],
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (!file.type.includes('json') && !file.name.endsWith('.json')) {
        setErrorMessage('Invalid file type. Please select a JSON file.')
        return
      }

      if (file.size > MAX_KEY_RING_FILE_SIZE) {
        setErrorMessage('File too large. Key ring files should be under 1MB.')
        return
      }

      void handleImport(file).finally(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      })
    },
    [handleImport],
  )

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRetry = useCallback(() => {
    resetOnboard()
    setPhase('idle')
    setErrorMessage(null)
  }, [resetOnboard])

  const handleSwitchToImport = useCallback(() => {
    setShowImportMode(true)
    setErrorMessage(null)
    setPhase('idle')
  }, [])

  const handleOpenLostKeysDialog = useCallback(() => {
    setShowLostKeysDialog(true)
  }, [])

  const handleCloseLostKeysDialog = useCallback(() => {
    setShowLostKeysDialog(false)
  }, [])

  const handleConfirmLostKeys = useCallback(() => {
    setShowLostKeysDialog(false)
    void handleGenerate()
  }, [handleGenerate])

  const handleStorageModeChange = useCallback((mode: StorageMode) => {
    setSelectedStorageMode(mode)
  }, [])

  const isDisabled =
    phase === 'generating' || phase === 'importing' || phase === 'saving' || isUpdating

  const displayError = errorMessage ?? onboardError

  if (showImportMode) {
    return (
      <Box>
        <WizardHeader
          icon={<RestorePageOutlined />}
          title="Restore Your Encryption Keys"
          description="Upload your key ring backup file to restore end-to-end encryption for your events."
        />

        {phase === 'importing' && (
          <Box sx={loadingContainerSx}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Validating file...
            </Typography>
          </Box>
        )}

        {displayError && (
          <Alert
            severity="error"
            sx={alertSx}
            action={
              phase === 'error' ? (
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {displayError}
          </Alert>
        )}

        <Box sx={actionContainerSx}>
          <Button
            variant="contained"
            size="large"
            onClick={handleFileSelect}
            disabled={isDisabled}
            fullWidth
          >
            Upload Key Ring File
          </Button>
        </Box>

        <Box sx={storageModeContainerSx}>
          <StorageModeCheckbox
            currentMode={selectedStorageMode}
            onModeChange={handleStorageModeChange}
            disabled={isDisabled}
          />
        </Box>

        <Box sx={linkContainerSx}>
          <Link
            component="button"
            variant="body2"
            onClick={handleOpenLostKeysDialog}
            underline="hover"
            sx={linkSx}
          >
            Lost your keys? Generate new ones
          </Link>
        </Box>

        <Dialog
          open={showLostKeysDialog}
          onClose={handleCloseLostKeysDialog}
          aria-labelledby="lost-keys-dialog-title"
          aria-describedby="lost-keys-dialog-description"
        >
          <DialogTitle id="lost-keys-dialog-title">Generate New Encryption Keys?</DialogTitle>
          <DialogContent>
            <DialogContentText id="lost-keys-dialog-description">
              Generating new keys means you will NOT be able to decrypt historical events encrypted
              with your previous keys. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLostKeysDialog}>Cancel</Button>
            <Button onClick={handleConfirmLostKeys} color="error" variant="contained">
              Generate New Keys
            </Button>
          </DialogActions>
        </Dialog>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileInputChange}
          style={hiddenInputStyle}
          aria-hidden="true"
          tabIndex={-1}
        />
      </Box>
    )
  }

  return (
    <Box>
      <WizardHeader
        icon={<Lock />}
        title="Set Up End-to-End Encryption"
        description="Generate encryption keys to protect your event data. A backup file will be downloaded automatically."
      />

      {phase === 'generating' && (
        <Box sx={loadingContainerSx}>
          <LinearProgress sx={progressSx} />
          <Typography variant="body2" color="text.secondary">
            Generating encryption keys...
          </Typography>
        </Box>
      )}

      {phase === 'saving' && !displayError && (
        <Box sx={loadingContainerSx}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Saving progress...
          </Typography>
        </Box>
      )}

      {displayError && (
        <Alert
          severity="error"
          sx={alertSx}
          action={
            phase === 'error' ? (
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {displayError}
        </Alert>
      )}

      <Box sx={actionContainerSx}>
        <Button
          variant="contained"
          size="large"
          onClick={handleGenerate}
          disabled={isDisabled}
          fullWidth
        >
          Generate Key Ring
        </Button>
      </Box>

      <Box sx={storageModeContainerSx}>
        <StorageModeCheckbox
          currentMode={selectedStorageMode}
          onModeChange={handleStorageModeChange}
          disabled={isDisabled}
        />
      </Box>

      <Box sx={linkContainerSx}>
        <Link
          component="button"
          variant="body2"
          onClick={handleSwitchToImport}
          underline="hover"
          sx={linkSx}
        >
          Already have keys? Import them
        </Link>
      </Box>
    </Box>
  )
}

const loadingContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1.5,
  mb: 3,
}

const progressSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 300,
}

const alertSx: SxProps<Theme> = {
  mb: 3,
}

const actionContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  px: 4,
}

const storageModeContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 2,
  px: 4,
}

const linkContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 2,
}

const linkSx: SxProps<Theme> = {
  cursor: 'pointer',
}

const hiddenInputStyle: React.CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  overflow: 'hidden',
  opacity: 0,
}
